// SPDX-License-Identifier: SEE LICENSE
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Layout } from '../components/Layout';
import { CertificateDoc, type CertDocData } from '../components/CertificateDoc';
import { BACKEND, getSessionCertificate, mintSessionCertificate } from '../api';

interface PillarEntry {
  pillar: string;
  title: string;
  verdict: string;
  headline?: string;
  runId?: string;
  merkleLeaf: string;
}

interface CertView {
  short?: string;
  merkle_root_hex?: string;
  overall?: string;
  key_attested?: boolean;
  pdf_url?: string;
  bundle?: {
    credentialSubject?: {
      device?: { manufacturer?: string; model?: string };
      sessionMerkleRoot?: string;
      sessionMerkleRootSource?: string;
      overallVerdict?: string;
      verdictCounts?: Record<string, number>;
      pillars?: PillarEntry[];
      deviceAttestation?: {
        keyAttested?: boolean;
        keyFingerprintSha256?: string;
        sessionRootSignature?: string;
      };
      anchors?: {
        rekorUrl?: string;
        rekorLogIndex?: string;
        openTimestampsCalendars?: string[];
        openTimestampsStatus?: string;
      };
    };
    proof?: { proofValue?: string; verificationMethod?: string };
  };
}

const verdictTone: Record<string, string> = {
  pass: 'border-pass/50 bg-pass/10 text-pass',
  concern: 'border-amber-400/50 bg-amber-400/10 text-amber-700',
  fail: 'border-fail/50 bg-fail/10 text-fail',
  incomplete: 'border-slate-300 bg-slate-50 text-slate-600',
};


export function Certificate() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // ?v=1 → clean public view (what the QR opens): just the certificate, no
  // editing chrome — so a scan shows exactly the on-screen template.
  const embed = searchParams.get('v') === '1';
  const [cert, setCert] = useState<CertView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    getSessionCertificate(id)
      .then((c) => setCert(c as CertView))
      .catch((e) => setError(String(e)));
  }, [id]);

  useEffect(load, [load]);

  const mint = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await mintSessionCertificate(id);
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const subject = cert?.bundle?.credentialSubject;
  const overall = cert?.overall ?? subject?.overallVerdict ?? 'incomplete';
  const pdfHref = cert?.pdf_url ? `${BACKEND}${cert.pdf_url}` : null;

  // Public scan view: the certificate alone, no editing chrome.
  if (embed) {
    return (
      <Layout hideHeader>
        {error && (
          <div className="rounded-lg border border-fail/40 bg-fail/10 px-4 py-3 text-sm text-fail">{error}</div>
        )}
        {cert ? (
          <div className="animate-fadein">
            <CertificateDoc cert={cert as CertDocData} sessionId={id} />
          </div>
        ) : !error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading certificate…</div>
        ) : null}
      </Layout>
    );
  }

  return (
    <Layout
      right={
        <Link to={`/sessions/${id}`} className="text-xs font-semibold text-slate-600 hover:text-slate-900">
          ← Session
        </Link>
      }
    >
      <div className="mb-5 flex items-start justify-between animate-fadein">
        <div>
          <div className="eyebrow">Composite credential</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Digital Device Certificate</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Aggregates every pillar verdict under a single hardware-bound session Merkle root, signed
            with the P-384 Retronics key and anchored to Sigstore Rekor + OpenTimestamps.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
            verdictTone[overall] ?? verdictTone.incomplete
          }`}
        >
          {overall}
        </span>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={mint}
          disabled={busy}
          className="rounded-lg border border-gold/40 bg-gold/10 px-4 py-2 text-sm font-semibold text-gold glass-hover disabled:opacity-50"
        >
          {busy ? 'Minting…' : 'Mint / refresh certificate'}
        </button>
        <button
          onClick={load}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 glass-hover"
        >
          Reload
        </button>
        {pdfHref && (
          <a
            href={pdfHref}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 glass-hover"
          >
            Open PDF ↗
          </a>
        )}
        <button
          onClick={() => setShowJson((v) => !v)}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500 glass-hover"
        >
          {showJson ? 'Hide raw VC' : 'Show raw VC'}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-fail/40 bg-fail/10 px-4 py-3 text-sm text-fail">
          {error}
        </div>
      )}

      {cert && (
        <div className="mt-5 animate-fadein">
          <CertificateDoc cert={cert as CertDocData} sessionId={id} />
        </div>
      )}

      {cert && showJson && (
        <pre className="glass mt-5 max-h-[50vh] overflow-auto p-4 font-mono text-[11px] leading-relaxed text-slate-600">
          {JSON.stringify(cert.bundle ?? cert, null, 2)}
        </pre>
      )}
    </Layout>
  );
}

